import _ from "lodash";
import { CFType } from "../../constants/cf_types";
import { requireArgument } from "../core/core";
import { llPrecedence } from "../core/ll";
import { user } from "../core/user";
import * as zc from "./codes";
import * as zs from "./schemas";

var entityNotFound = "Entity '{0}' was not found in schemas",
    propertyNotFound = "Property '{0}' was not found in schema for '{1}'",
    navigationPropertyNotFound = "Navigation property '{0}' was not found in schema for '{1}'";

/**
 * Joins any number of select/expand paths together with "/".
 * @params {(...string|string[])} paths Paths to join together (e.g. "Order", "Site/OwningCustomer", "CustomerName")
 * @returns {string} Combined path (e.g. "Order/Site/OwningCustomer/CustomerName")
 */
export function combinePaths(...paths);
export function combinePaths(paths) {
    var actualPaths = _.isArray(paths)
        ? paths
        : arguments;
    return _.filter(actualPaths, function (path) { return path != ""; }).join("/");
}

/**
 * Returns the localized name for a given entity.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "ContractSchedule")
 * @returns {string} The localized property name (e.g. "Contract Schedule"), or the unlocalized property name if no localization exists.
 * @throws {Error} Throws an error if entity is not found
 */
export function localizeEntity(args) {
    var precedence = getLocalizationPrecedenceForEntity(args);
    return llPrecedence({ precedence: precedence, defaultString: args.entityName });
}

/**
 * Returns the localized property name for a given entity and property.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Customer")
 * @param {string} args.field Access path of the field (e.g. "CustomerName", "BillTo.SiteNumber", or "BillTo.ShipTo.ShipToNumber")
 * @returns {string} The localized property name (e.g. "Customer Name"), or the unlocalized property name if no localization exists (e.g. "DeletedDateTime")
 * @throws {Error} Throws an error if entity or property is not found
 */
export function localizeProperty(args: { entityName: string, field: string, schemas?: { [name: string]: any } }) {
    const precedence = getLocalizationPrecedenceForProperty(args),
        propertyName = _.last(args.field.split("."));

    return llPrecedence({
        precedence: precedence,
        defaultString: propertyName
    });
}

/**
 * Returns the localized property name for a given entity and navigation property.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Site")
 * @param {string} args.navigationPropertyName Name of the property (e.g. "OwningCustomer")
 * @returns {string} The localized property name (e.g. "Owning Customer"), or the unlocalized property name if no localization exists
 * @throws {Error} Throws an error if entity or navigation property is not found
 */
export function localizeNavigationProperty(args) {
    var precedence = getLocalizationPrecedenceForNavigationProperty(args);
    return llPrecedence({ precedence: precedence, defaultString: args.navigationPropertyName });
}

/**
 * Returns an array of the order we should try looking for strings in LocalizedLanguage for a given entity.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Customer")
 * @param {object} [args.schemas] Schemas to use
 * @returns {string[]} Array of names to try to localize in order (e.g. ["Entity.Customer", "Customer"])
 * @throws {Error} Throws an error if entity is not found
 */
export function getLocalizationPrecedenceForEntity(args) {
    args = args || {};

    var entityName = requireArgument(args, "entityName"),
        schemas = args.schemas || zs.schemas || {},
        schema = getSchema(entityName, schemas);

    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    return [`Entity.${entityName}`, entityName];
}

/**
 * Returns an array of the order we should try looking for strings in LocalizedLanguage for a given entity and property.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Customer")
 * @param {string} args.field Access path of the field (e.g. "CustomerName", "BillTo.SiteNumber", or "BillTo.ShipTo.ShipToNumber")
 * @param {object} [args.schemas] Schemas to use
 * @returns {string[]} Array of names to try to localize in order (e.g. ["Customer.CustomerName", "Customer"])
 */
export function getLocalizationPrecedenceForProperty(args: { entityName: string, field: string, schemas?: { [name: string]: any } }) {
    const { entityName, field } = args,
        schemas = args.schemas || zs.schemas || {};

    let schema = getSchema(entityName, schemas);
    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    if (!_.includes(field, ".")) {
        var navigationPropertyName = getMappedNavigationPropertyName({
            entityName: entityName,
            propertyName: field,
            schemas: schemas
        });
        if (navigationPropertyName) {
            return getLocalizationPrecedenceForNavigationProperty({
                entityName: entityName,
                navigationPropertyName: navigationPropertyName,
                schemas: schemas
            });
        }
    }

    // e.g. path = ["Customer", "BillTo", "ShipTo", "ShipToNumber"]
    const path = [entityName, ...field.split(".")];

    // e.g. precedence = ["Customer.BillTo.ShipTo.ShipToNumber", BillTo.ShipTo.ShipToNumber", "ShipTo.ShipToNumber", "ShipToNumber"]
    let precedence = _.range(path.length)
        .map(i => _.takeRight(path, i + 1))
        .map(x => x.join("."))
        .reverse();

    if (_.endsWith(field, "Id")) {
        // try with and without the "Id" at the end
        precedence = _.flatMap(precedence, x => [x, x.slice(0, -2)]);
    }

    return precedence;
}

/**
 * Returns an array of the order we should try looking for strings in LocalizedLanguage for a given entity and navigation property.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Site")
 * @param {string} args.navigationPropertyName Name of the property (e.g. "OwningCustomer")
 * @param {object} [args.schemas] Schemas to use
 * @returns {string[]} Array of names to try to localize in order (e.g. ["Site.OwningCustomer", "OwningCustomer", "Entity.Customer", "Customer"])
 * @throws {Error} Throws an error if entity or navigation property is not found
 */
export function getLocalizationPrecedenceForNavigationProperty(args) {
    args = args || {};

    var entityName = requireArgument(args, "entityName"),
        navigationPropertyName = requireArgument(args, "navigationPropertyName"),
        schemas = args.schemas || zs.schemas || {},
        schema = getSchema(entityName, schemas);

    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    var navigationProperty = schema.NavigationProperties[navigationPropertyName];
    if (!navigationProperty) {
        throw new Error(navigationPropertyNotFound.format(navigationPropertyName, entityName));
    }

    if (navigationProperty.IsCollection) {
        if (navigationPropertyName == navigationProperty.Type) {
            return _.uniq([`${entityName}.${navigationPropertyName}`, `Entity.${navigationProperty.Type}`, navigationProperty.Type]);
        }
        return _.uniq([`${entityName}.${navigationPropertyName}`, navigationPropertyName, `Entity.${navigationProperty.Type}`, navigationProperty.Type]);
    }

    var mapTo = navigationProperty.MapTo;
    if (_.endsWith(mapTo, "Id")) {
        mapTo = mapTo.slice(0, -2);
    }

    if (navigationPropertyName == navigationProperty.Type) {
        if (mapTo == navigationProperty.Type) {
            return _.uniq([`${entityName}.${navigationPropertyName}`, `Entity.${navigationProperty.Type}`, navigationProperty.Type]);
        }
        return _.uniq([`${entityName}.${navigationPropertyName}`, `${entityName}.${mapTo}`, mapTo, `Entity.${navigationProperty.Type}`, navigationProperty.Type]);
    }
    return _.uniq([`${entityName}.${navigationPropertyName}`, `${entityName}.${mapTo}`, mapTo, navigationPropertyName, `Entity.${navigationProperty.Type}`, navigationProperty.Type]);
}

/**
 * Returns the name of the navigation property corresponding to the given entity and property.
 *
 * e.g. { entityName: "Site", propertyName: "OwningCustomerId" } => "OwningCustomer",
 * meaning that I would have to expand on OwningCustomer to get information about the customer.
 *
 * If there are multiple navigation properties corresponding to the property, preference is given to
 * the navigation property that matches the property name.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Site")
 * @param {string} args.propertyName Name of the property (e.g. "OwningCustomerId")
 * @param {object} [args.schemas] Schemas to use
 * @returns {(string|null)} Name of the matching navigation property (e.g. "OwningCustomer") or null if none is found
 */
export function getMappedNavigationPropertyName(args) {
    args = args || {};

    var entityName = requireArgument(args, "entityName"),
        propertyName = requireArgument(args, "propertyName"),
        schemas = args.schemas || zs.schemas || {},
        schema = getSchema(entityName, schemas);

    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    var navigationProperties = schema.NavigationProperties || {},
        navigationPropertyNames = _(navigationProperties)
            .keys()
            .filter(function (name) {
                return !navigationProperties[name].IsCollection && navigationProperties[name].MapTo == propertyName;
            })
            .value();

    var matchingName = _.find(navigationPropertyNames, function (name) {
        return propertyName == `${name}Id`;
    });

    return matchingName || _.head(navigationPropertyNames) || null;
}

/**
 * Returns an array containing the primary and secondary display fields for an entity, with the primary display fields listed first.
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Customer")
 * @param {object} [args.schemas] Schemas to use
 * @returns {string[]} The array of display field property names (e.g. ["CustomerNumber", "CustomerName", "SortName"])
 * @throws {Error} Throws an error if entity is not found
 */
export function getDisplayFields(args) {
    args = args || {};

    var entityName = requireArgument(args, "entityName"),
        schemas = args.schemas || zs.schemas || {},
        schema = getSchema(entityName, schemas);

    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    var displayProperties = _.pickBy(schema.Properties, function (p) { return p.Display; }) as {},
        displayFields = _(displayProperties)
            .keys()
            .sortBy(function (name) { return displayProperties[name].Display; })
            .value() as string[];

    return displayFields;
}

/**
 * Returns an object containing the primary and secondary display fields for a code entity
 * @param {object} args
 * @param {string} args.entityName Name of the entity (e.g. "Region")
 * @param {object} [args.schemas] Schemas to use
 * @returns {object} The display fields (e.g. { primaryField: "RegionNumber", secondaryField: "RegionName" })
 * @throws {Error} Throws an error if entity is not found
 */
export function getCodeDisplayFields(args) {
    args = args || {};

    var entityName = requireArgument(args, "entityName"),
        schemas = args.schemas || zs.schemas || {},
        schema = getSchema(entityName, schemas);

    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    if (schema.Properties.CodeNumber) {
        return { primaryField: "CodeNumber", secondaryField: "Value" };
    }

    var displayFields = getDisplayFields({
        entityName: entityName,
        schemas: schemas
    });
    return {
        primaryField: displayFields[0] || "CodeNumber",
        secondaryField: displayFields[1] || "Value"
    };
}

/**
 * Get information about a property from the schema.
 *
 * For example, the following calls are equivalent:
 * z.schema.getPropertyInfo({ "entityName": "Order", "path": "Site/OwningCustomer/CustomerNumber" })
 * z.schema.getPropertyInfo({ "path": "Order/Site/OwningCustomer/CustomerNumber" })
 * z.schema.getPropertyInfo("Order/Site/OwningCustomer/CustomerNumber")
 *
 * The call returns:
 * {
 *     propertyName: "CustomerNumber"                              // the name of the property
 *     entityName: "Customer"                                      // the entity the property is on
 *     property: { ... }                                           // the property info (i.e. z.getSchema(entityName, schemas).Properties[propertyName])
 *     selectPath: "Site/OwningCustomer/CustomerNumber",           // how you would select this property in an OData query on "Order"
 *     fullSelectPath: "Order/Site/OwningCustomer/CustomerNumber", // same as selectPath, except with "Order" included in the path
 *     expandPath: "Site/OwningCustomer",                          // how you would expand in an OData query on "Order" to get this property
 *     fullExpandPath: "Order/Site/OwningCustomer",                // same as expandPath, except with "Order" included in the path
 *     accessPath: "Site.OwningCustomer.CustomerNumber"            // how you would access the property from the results
 * }
 *
 * @param {(object|string)} args
 * @param {string} [args.entityName] Name of the base entity; if not provided, the first part of the path should be the entity name.
 * @param {string} args.path Path to follow from the base entity that leads to a property.
 * @returns {object} Information about the property
 */
export function getPropertyInfo(args) {
    args = args || {};

    if (_.isString(args)) {
        args = { path: args };
    }

    var path = requireArgument(args, "path"),
        accessPath = _getAccessPath(args.entityName, path);

    path = normalizePath(path);
    var entityName = args.entityName,
        schemas = args.schemas || zs.schemas || {};

    if (!entityName) {
        var entitySplit = splitFirst(path);
        entityName = entitySplit[0];
        path = entitySplit[1];
    }

    var topEntityName = entityName,
        split = splitLast(path),
        propertyName = split[1];

    if (split[0]) {
        var result = getNavigationPropertyInfo({
            entityName: entityName,
            path: split[0],
            schemas: schemas
        });
        entityName = result.navigationProperty.Type;
    }

    var selectPathSplit = splitLast(path),
        fullSelectPath = `${topEntityName}/${path}`,
        fullSelectPathSplit = splitLast(fullSelectPath);

    if (propertyName && propertyName.toLowerCase().startsWith("udf_")) {

        const udf = user.companyData.UserDefinedFields
            .find(f =>
                f.TableName == entityName
                && f.BindName == propertyName);

        return {
            selectPath: path,
            expandPath: selectPathSplit[0],
            fullSelectPath: fullSelectPath,
            fullExpandPath: fullSelectPathSplit[0],
            accessPath: accessPath,
            entityName: entityName,
            propertyName: propertyName,
            property: {
                Required: udf.Required,
                Type: getUdfType({
                    entityName,
                    propertyName
                }),
                IsUDF: true
            }
        };
    }

    var schema = getSchema(entityName, schemas);
    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    var property = schema.Properties[propertyName];
    if (!property) {
        throw new Error(propertyNotFound.format(propertyName, entityName));
    }

    return {
        selectPath: path,
        expandPath: selectPathSplit[0],
        fullSelectPath: fullSelectPath,
        fullExpandPath: fullSelectPathSplit[0],
        accessPath: accessPath,
        entityName: entityName,
        propertyName: propertyName,
        property: property
    };
}

/**
 * Get information about a navigation property from the schema.
 *
 * For example, the following calls are equivalent:
 * z.schema.getNavigationPropertyInfo({ "entityName": "Order", "path": "Site/OwningCustomer/CustomerType" })
 * z.schema.getNavigationPropertyInfo({ "path": "Order/Site/OwningCustomer/CustomerType" })
 * z.schema.getNavigationPropertyInfo("Order/Site/OwningCustomer/CustomerType")
 *
 * The call returns:
 * {
 *     navigationPropertyName: "CustomerType"                      // the name of the navigation property
 *     entityName: "CustomerType"                                  // the entity the property refers to
 *     parentEntityName: "Customer"                                // the entity the property is on
 *     navigationProperty: { ... }                                 // the navigation property info (i.e. z.schemas[parentEntityName].NavigationProperties[navigationPropertyName])
 *     expandPath: "Site/OwningCustomer/CustomerType",             // how you would expand in an OData query on "Order" to expand on this navigation property
 *     fullExpandPath: "Order/Site/OwningCustomer/CustomerType",   // same as expandPath, except with "Order" included in the path
 *     accessPath: "Site.OwningCustomer.CustomerType"              // how you would access the navigation property from the results
 * }
 *
 * @param {(object|string)} args
 * @param {string} [args.entityName] Name of the base entity; if not provided, the first part of the path should be the entity name.
 * @param {string} args.path Path to follow from the base entity that leads to a navigation property.
 * @returns {object} Information about the navigation property
 */
export function getNavigationPropertyInfo(args) {
    args = args || {};

    if (_.isString(args)) {
        args = { path: args };
    }

    var path = requireArgument(args, "path"),
        accessPath = _getAccessPath(args.entityName, path);

    path = normalizePath(path);
    var entityName = args.entityName,
        schemas = args.schemas || zs.schemas || {};

    if (!entityName) {
        var entitySplit = splitFirst(path);
        entityName = entitySplit[0];
        path = entitySplit[1];
    }

    var topEntityName = entityName,
        split = splitLast(path),
        navigationPropertyName = split[1];

    if (split[0]) {
        var result = getNavigationPropertyInfo({
            entityName: entityName,
            path: split[0],
            schemas: schemas
        });
        entityName = result.navigationProperty.Type;
    }

    var schema = getSchema(entityName, schemas);
    if (!schema) {
        throw new Error(entityNotFound.format(entityName));
    }

    var navigationProperty = schema.NavigationProperties[navigationPropertyName];
    if (!navigationProperty) {
        throw new Error(navigationPropertyNotFound.format(navigationPropertyName, entityName));
    }

    return {
        expandPath: path,
        fullExpandPath: `${topEntityName}/${path}`,
        accessPath: accessPath,
        entityName: navigationProperty.Type,
        parentEntityName: entityName,
        navigationPropertyName: navigationPropertyName,
        navigationProperty: navigationProperty
    };
}

function _getAccessPath(entityName, path) {
    path = (entityName ? entityName + "/" : "") + path;
    path = path.replaceAll("/", ".");
    var index = path.indexOf(".");
    return path.slice(index + 1);
}

/**
 * Normalizes a path to be suitable for OData. Replaces "." with "/" and removes "[0]".
 * @param {string} path Path to be normalized
 * @returns {string} The normalized path
 */
export function normalizePath(path) {
    return path.replaceAll(".", "/").replaceAll("[0]", "");
}

/**
 * Given a select/expand path, returns an array containing two elements: the part before the first / and the part after.
 * @param {string} path The select/expand path (e.g. "Site/OwningCustomer/CustomerName")
 * @returns {string[]} The split path (e.g. ["Site", "OwningCustomer/CustomerName"])
 */
export function splitFirst(path) {
    var index = path.indexOf("/");
    if (index != -1) {
        return [path.substr(0, index), path.slice(index + 1)];
    }
    return [path, ""];
}

/**
 * Given a select/expand path, returns an array containing two elements: the part before the last / and the part after.
 * @param {string} path The select/expand path (e.g. "Site/OwningCustomer/CustomerName")
 * @returns {string[]} The split path (e.g. ["Site/OwningCustomer", "CustomerName"])
 */
export function splitLast(path) {
    var index = path.lastIndexOf("/");
    if (index != -1) {
        return [path.substr(0, index), path.slice(index + 1)];
    }
    return ["", path];
}

/**
 * For a given array of select paths, returns the columns that are available to select, "expanding out" any select "*"s.
 * If you include a select "*" the list will include the primary and secondary field for any codes as well as any UDFs.
 * @param {object} args
 * @param {string} args.entityName The base entity name (e.g. "Order")
 * @param {string[]} args.selectPaths An array containing the paths to select (e.g. ["*", "Site/*", "Site/OwningCustomer/CustomerNumber"])
 * @param {object} [args.schemas] Schemas to use
 * @params {object} [args.codes] Codes to use
 * @params {object} [args.userDefinedFields] User defined fields to use
 * @returns {string[]} Array of available select paths (e.g. ["Id", "OrderNumber", ..., "OrderStatus/CodeNumber", "OrderStatus/Value", ..., "UDF_Custom", ..., "Site/Id", "Site/SiteNumber", ..., "Site/SAC/CodeNumber", "Site/SAC/Value", ..., "Site/UDF_Custom2", ..., "Site/OwningCustomer/CustomerNumber"])
 */
export function getAvailableSelectPaths(args) {
    args = args || {};

    var entityName = requireArgument(args, "entityName"),
        selectPaths = requireArgument(args, "selectPaths"),
        schemas = args.schemas || zs.schemas || {},
        codes = args.codes || zc.codes || {},
        userDefinedFields: any[] = args.userDefinedFields || user.companyData.UserDefinedFields || {};

    var properties = _.flatMap(selectPaths, function (path) {
        if (_.endsWith(path, "*")) {
            path = path.slice(0, -1);
            if (_.endsWith(path, "/")) {
                path = path.slice(0, -1);
            }
            var selectEntityName = path
                ? getNavigationPropertyInfo({
                        entityName: entityName,
                        path: path
                    }).entityName
                : entityName;
            var allProperties = [];
            var schema = getSchema(selectEntityName, schemas);

            _.forEach(_.keys(schema.Properties), function (propertyName) {
                var property = schema.Properties[propertyName],
                    codeEntityName = property.ForeignKeyType;
                if (codeEntityName) {
                    if (codes[codeEntityName]) {
                        var navigationPropertyName = getMappedNavigationPropertyName({
                                entityName: selectEntityName,
                                propertyName: propertyName,
                                schemas: schemas
                            }),
                            codeDisplayFields = getCodeDisplayFields({
                                entityName: codeEntityName,
                                schemas: schemas
                            }),
                            codeDisplayFieldsArray = _.map(codeDisplayFields, function (field) {
                                return combinePaths(path, navigationPropertyName, field);
                            });
                        allProperties = _.concat(allProperties, codeDisplayFieldsArray);
                    }
                } else {
                    allProperties.push(combinePaths(path, propertyName));
                }
            });
            var entityUserDefinedFields = _.filter(userDefinedFields, function (udf) {
                return udf.TableName == selectEntityName;
            });
            _.forEach(entityUserDefinedFields, function (udf) {
                if (udf.Type == "ENUM") {
                    var navigationPropertyName = udf.BindName.replace("UDF_", "UDFLV_"),
                        codeDisplayFields = ["CodeNumber", "Value"];
                    codeDisplayFields = _.map(codeDisplayFields, function (field) {
                        return combinePaths(path, navigationPropertyName, field);
                    });
                    allProperties = _.concat(allProperties, codeDisplayFields);
                } else {
                    allProperties.push(combinePaths(path, udf.BindName));
                }
            });

            return allProperties;
        } else {
            return [path];
        }
    });

    return _.uniq(properties);
}

export function getSchema(entityName: string, schemas: any = zs.schemas) {
    if (entityName.endsWith("_history")) {
        let index = entityName.indexOf("_history");
        let entity = entityName.substring(0, index);
        let schema = _.cloneDeep(schemas[entity]);

        schema.Properties = {
            AuditId: { Type: "Int32", Required: true },
            AuditDateTime: { Type: "DateTime", Required: true },
            ...schema.Properties
        };

        return schema;
    } else {
        return schemas[entityName];
    }
}

export function getUdfType(schemaPropertyInfo: any) {
    if (
        schemaPropertyInfo &&
        schemaPropertyInfo.property &&
        schemaPropertyInfo.property.Type
    ) {
        if (schemaPropertyInfo.property.Type == "UDFDate") {
            return "Date";
        }
        if (schemaPropertyInfo.property.Type == "UDFTime") {
            return "TimeOfDay";
        }
        if (schemaPropertyInfo.property.Type == "UDFDateTime") {
            return "DateTime";
        }
    }

    let entity = schemaPropertyInfo.entityName,
        field = schemaPropertyInfo.propertyName;

    if (field && field.toLowerCase().startsWith("udf_")) {
        let customfield = _.find(user.companyData.UserDefinedFields, {
            TableName: entity,
            BindName: field
        });

        if (customfield) {
            let type = customfield.Type as CFType;
            if (type == CFType.DATE) {
                return "Date";
            } else if (type == CFType.DATETIME) {
                return "DateTime";
            } else if (type == CFType.TIME) {
                return "TimeOfDay";
            } else if (type == CFType.CHECKBOX) {
                return "Boolean";
            } else if (type == CFType.INT) {
                return "Int32";
            } else if (type == CFType.FLOAT) {
                return "Decimal";
            }
        }
    }
}